import { recordAttributionPurchase } from '@/lib/crm-service'
/**
 * Builds and sends the dataLayer `purchase` event(s) for one paid Stripe checkout session.
 *
 * A basket spanning products reports a purchase per product line (membership, test kit, Daily30,
 * bars), so one session can produce more than one event.
 */
import {
  getDaily30BarsBundle,
  getBarsFlavourFromLookupKey,
  mapBarsItems,
  trackBarsPurchaseEvent,
} from '@/lib/gtm/barsGtm'
import { trackDaily30PurchaseEvent } from '@/lib/gtm/daily30Gtm'
import { trackGutHealthTestPurchaseEvent, trackMembershipPurchaseEvent } from '@/lib/gtm/membershipGtm'
import { hashEmail } from '@/lib/utils/cryptoUtils'
import { getPricesTrackingData, priceRecurringIntervalToPeriod } from '@/lib/utils/getPricesTrackingData'

import { type GetCheckoutSessionDetailsResponse, PriceProductType } from '@/clients/payment-service'

import { combineComplexTrialLineItems, normalizeMembershipTrackingItems } from './complexTrialUtils'
import { excludePaymentApiShippingFeeLineItems } from './shippingFeeLineItems'
import { hasTrackedPurchase, markPurchaseTracked } from './trackedPurchaseSessions'
import type { CheckoutPath } from './types'

export type PurchaseUserType = 'New' | 'Existing' | 'Guest'

type TrackSessionPurchaseProps = {
  session: GetCheckoutSessionDetailsResponse
  countryCode?: string
  userType: PurchaseUserType
  /** Plain email; hashed inside the GTM builders. */
  userEmail?: string
  checkoutPath: CheckoutPath
}

export const trackSessionPurchaseEvents = ({
  session,
  countryCode,
  userType,
  userEmail,
  checkoutPath,
}: TrackSessionPurchaseProps): boolean => {
  const sessionLineItems = excludePaymentApiShippingFeeLineItems(session.line_items ?? [])
  if (sessionLineItems.length === 0) return false
  if (hasTrackedPurchase(session.session_id)) return false

  // Marked first so a throw in a builder can't leave the session reportable again.
  markPurchaseTracked(session.session_id)

  const currency = sessionLineItems[0].price.currency
  const country = countryCode || session.customer?.shipping_address?.country_code || ''

  const userData = {
    first_name: session.customer?.first_name || '',
    last_name: session.customer?.last_name || '',
    email: hashEmail(session.customer?.email ?? '') || '',
    // WARN: Required but not use in checkout.
    phone_number: '',
    country: session.customer?.shipping_address?.country_code || '',
    city: session.customer?.shipping_address?.city || '',
    postal_code: session.customer?.shipping_address?.postal_code || '',
  }

  const email = userEmail || session.customer?.email || undefined
  const sessionTax = (session.amount_tax || 0) / 100
  const sessionTotal = (session.amount_total || 0) / 100

  const hasMembership = sessionLineItems.some((li) => li.price.lookup_key.includes('ZOE_PLUS'))
  const hasTestKit = sessionLineItems.some((li) => li.price.product_type === PriceProductType.GUT_MICROBIOME_TEST)
  const hasDaily30 = sessionLineItems.some((li) => li.price.lookup_key.includes('DAILY30'))
  const hasBars = sessionLineItems.some((li) => li.price.lookup_key.includes('BARS'))

  // Bars sold alongside Daily30 are the welcome bundle: one purchase event,
  // bars as a line inside the Daily30 event. Bars without D30 are standalone.
  const isWelcomeBundle = hasDaily30 && hasBars
  const buildBarsPlanItems = (itemBundle?: string) =>
    sessionLineItems
      .filter((li) => li.price.lookup_key.includes('BARS'))
      .map((item) => ({
        ...(itemBundle && { item_bundle: itemBundle }),
        item_category2: getBarsFlavourFromLookupKey(item.price.lookup_key),
        item_category3: priceRecurringIntervalToPeriod(item.price.recurring),
        item_category4: country,
        price: (item.price.unit_amount ?? 0) / 100,
        ...(item.amount_discount ? { discount: item.amount_discount / (item.quantity || 1) / 100 } : {}),
        quantity: item.quantity,
      }))

  if (hasMembership) {
    const { combined, normalizedKeys, itemBundle } = normalizeMembershipTrackingItems(sessionLineItems)
    const sessionItems = combined.map((item, idx) => ({
      item_bundle: itemBundle,
      item_id: normalizedKeys[idx],
      item_name: getPricesTrackingData([item.price])[item.price.lookup_key]?.nickname || '',
      item_category3: priceRecurringIntervalToPeriod(item.dueNext?.recurring ?? item.price.recurring),
      item_category4: country,
      price: ((item.amount_subtotal || 0) - (item.amount_discount || 0)) / 100,
      quantity: 1,
      ...(item.trialLengthDays && { trialLengthDays: item.trialLengthDays }),
    }))
    trackMembershipPurchaseEvent({
      user_type: userType,
      user_email: email,
      currency,
      tax: sessionTax,
      transaction_id: session.session_id,
      value: sessionTotal,
      payment_type: session.payment_method_type ?? '',
      items: sessionItems,
      user_data: userData,
      checkout_path: checkoutPath,
    })
  }

  if (hasTestKit) {
    const item = sessionLineItems.find((li) => li.price.product_type === PriceProductType.GUT_MICROBIOME_TEST)
    if (item) {
      trackGutHealthTestPurchaseEvent({
        user_type: 'Existing',
        user_email: email,
        currency,
        lookup_key: item.price.lookup_key,
        tax: sessionTax,
        shipping: 0,
        coupon: '',
        transaction_id: session.session_id,
        value: sessionTotal,
        shipping_tier: '',
        payment_type: session.payment_method_type ?? '',
        user_data: userData,
        checkout_path: checkoutPath,
        country: session.customer?.shipping_address?.country_code || '',
        price: ((item.amount_subtotal || 0) - (item.amount_discount || 0)) / 100,
      })
    }
  }

  if (hasDaily30) {
    // Bars are tracked as their own line and never part of a complex trial
    // pair, so drop them before combining. Combining the remaining pairs lets
    // the purchase event emit a single item with trial metadata.
    const combinedD30 = combineComplexTrialLineItems(
      sessionLineItems.filter((li) => !li.price.lookup_key.includes('BARS')),
    )
    const d30ItemBundle = combinedD30
      .map((item) => item.price.lookup_key)
      .slice()
      .sort()
      .join('-')
    const itemBundleName = isWelcomeBundle ? getDaily30BarsBundle(country) : d30ItemBundle
    const d30Items = combinedD30.map((item) => ({
      item_bundle: itemBundleName,
      item_id: item.price.lookup_key || '',
      item_name: getPricesTrackingData([item.price])[item.price.lookup_key]?.nickname || '',
      item_category3: priceRecurringIntervalToPeriod(item.dueNext?.recurring ?? item.price.recurring),
      item_category4: country,
      price: ((item.amount_subtotal || 0) - (item.amount_discount || 0)) / 100,
      quantity: 1,
      ...('trialLengthDays' in item && item.trialLengthDays ? { trialLengthDays: item.trialLengthDays } : {}),
    }))
    trackDaily30PurchaseEvent({
      user_type: userType,
      user_email: email,
      currency,
      tax: sessionTax,
      transaction_id: session.session_id,
      value: sessionTotal,
      payment_type: session.payment_method_type ?? '',
      items: d30Items,
      user_data: userData,
      checkout_path: checkoutPath,
      productSelected: d30ItemBundle,
      // Fold the bars line into the same event so the welcome bundle is one purchase.
      additionalItems: isWelcomeBundle ? mapBarsItems(buildBarsPlanItems(getDaily30BarsBundle(country))) : undefined,
    })
  }

  // Standalone bars purchase (PDP flow) — its own purchase event, no bundle.
  // In the welcome bundle the bars ride inside the Daily30 event above.
  if (hasBars && !hasDaily30) {
    trackBarsPurchaseEvent({
      user_type: userType,
      user_email: email,
      currency,
      tax: sessionTax,
      transaction_id: session.session_id,
      value: sessionTotal,
      payment_type: session.payment_method_type ?? '',
      items: buildBarsPlanItems(),
      user_data: userData,
      checkout_path: checkoutPath,
    })
  }

  return true
}

/**
 * Records the purchase against the attribution lead_id, merged across every session on this URL.
 *
 * Merged, not one call per session: crm-service overwrites product/value for a lead_id rather than
 * accumulating, so the row must carry the whole basket or a cross-sell write would replace the
 * membership purchase it followed. Best-effort — recordAttributionPurchase swallows its failures.
 */
export const recordMergedAttributionPurchase = (
  sessions: GetCheckoutSessionDetailsResponse[],
  leadId: string | null,
): void => {
  if (!leadId || sessions.length === 0) return

  const lineItems = sessions.flatMap((session) => excludePaymentApiShippingFeeLineItems(session.line_items ?? []))
  // crm-service rejects an empty product (minLength 1).
  if (lineItems.length === 0) return

  const product = Array.from(new Set(lineItems.map((li) => li.price.product_type))).join(',')
  // value is what the customer paid (after discounts, incl. tax and shipping); product lists only
  // real products, so the two deliberately differ in basis.
  const value = sessions.reduce((sum, session) => sum + (session.amount_total ?? 0), 0) / 100
  void recordAttributionPurchase(leadId, product, value, lineItems[0].price.currency)
}
